import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ShareItem } from "../../../../../mocks/animals";
import { findCustomerByShare, getShareSlotNos } from "../../../../../lib/salesFlow";
import { useAnimals } from "../../../../../hooks/useAnimals";
import { useCustomers } from "../../../../../hooks/useCustomers";
import { usePayments } from "../../../../../hooks/usePayments";
import { getDisplayKupeNo, getDisplaySiparisNo } from "../../../../../lib/animalLabels";
import { analyzeShareSaleDeletion, deleteShareSaleCascade } from "../../../../../lib/cascadeOps";
import { getDeliveryAssignments, setAllDeliveryAssignments } from "../../../../../lib/deliveryStore";
import SharePaymentDrawer from "./SharePaymentDrawer";
import ShareTransferModal from "../../../../../components/transfer/ShareTransferModal";
import { applyOperationResult } from "../../../../../lib/operationRefresh";
import { deleteSaleOperationRequest } from "../../../../../lib/api";

const durumBadge: Record<
  string,
  { cls: string; label: string }
> = {
  Tam: { cls: "bg-farm-green-pale text-farm-green", label: "Tam" },
  Kısmi: { cls: "bg-farm-gold-pale text-farm-gold", label: "Kısmi" },
  Bekliyor: { cls: "bg-farm-earth-pale text-farm-earth", label: "Bekliyor" },
};

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

function FinRow({
  label,
  value,
  accent = false,
  muted = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="font-body text-[10px] text-farm-muted leading-none whitespace-nowrap">
        {label}
      </p>
      <p
        className={`font-body text-xs font-semibold leading-none whitespace-nowrap ${
          accent
            ? "text-farm-earth"
            : muted
            ? "text-farm-muted"
            : "text-farm-brown"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function SoldShareCard({
  share,
  animalId,
}: {
  share: ShareItem;
  animalId: number;
}) {
  const navigate = useNavigate();
  const { animals, setAllAnimals } = useAnimals();
  const { customers, setAllCustomers } = useCustomers();
  const { payments, setAllPayments } = usePayments();
  const [showPaymentDrawer, setShowPaymentDrawer] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const badge = durumBadge[share.durum];
  const slotNos = useMemo(() => getShareSlotNos(share), [share]);

  const toplamUcret =
    share.tutar +
    (share.kesimUcreti ?? 0) +
    (share.parcalamaUcreti ?? 0) +
    (share.tasimaUcreti ?? 0) -
    (share.indirim ?? 0);

  const hasFees =
    share.kesimUcreti !== undefined ||
    share.parcalamaUcreti !== undefined ||
    share.tasimaUcreti !== undefined ||
    share.indirim !== undefined;

  const matchedCustomer = findCustomerByShare(customers, share);

  async function handleDeleteSale() {
    const analysis = analyzeShareSaleDeletion({
      shareId: share.id,
      animals,
      payments,
    });

    if (!analysis.share || !analysis.animal) {
      window.alert("İptal edilecek hisse satışı bulunamadı.");
      return;
    }

    const ok = window.confirm(
      [
        `${analysis.share.hisseNo} satışı iptal edilsin mi?`,
        `Müşteri: ${analysis.share.musteriAdi}`,
        `Küpe No: ${getDisplayKupeNo(analysis.animal)}`,
        `Sipariş No: ${getDisplaySiparisNo(analysis.animal)}`,
        `Silinecek ödeme kaydı: ${analysis.paymentCount}`,
        "",
        "Bu işlem hisseyi boş slota çevirir ve bağlı ödemeleri siler.",
      ].join("\n")
    );

    if (!ok) return;

    try {
      const result = await deleteSaleOperationRequest(share.id);
      await applyOperationResult(result);
    } catch (error) {
      console.error(error);
      window.alert("Satış silinirken çakışma veya bağlantı hatası oluştu. Lütfen son veriyi kontrol edip tekrar deneyin.");
    }
  }

  return (
    <>
      <div
        className={`bg-farm-warm border rounded-farm overflow-hidden ${
          share.durum === "Bekliyor"
            ? "border-farm-earth/25"
            : "border-farm-border"
        }`}
      >
        <div className="flex items-center justify-between px-3 py-2.5 bg-farm-cream border-b border-farm-border">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="font-body text-[10px] text-farm-muted font-semibold whitespace-nowrap flex-shrink-0">
              {share.hisseNo}
            </span>
            <span className="font-body text-xs font-bold text-farm-brown truncate">
              {share.musteriAdi}
            </span>
            {share.hisseSayisi > 1 && (
              <span className="flex-shrink-0 font-body text-[10px] bg-farm-gold-pale text-farm-gold px-1.5 py-0.5 rounded whitespace-nowrap">
                {share.hisseSayisi} hisse • Slot {slotNos.join(", ")}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${badge.cls}`}
            >
              {badge.label}
            </span>
          </div>
        </div>

        {share.telefon && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-farm-border/50">
            <div className="w-3.5 h-3.5 flex items-center justify-center text-farm-muted">
              <i className="ri-phone-line text-xs" />
            </div>
            <a
              href={`tel:${share.telefon.replace(/\s/g, "")}`}
              className="font-body text-xs text-farm-earth hover:text-farm-brown transition-colors cursor-pointer"
            >
              {share.telefon}
            </a>
          </div>
        )}

        <div className="px-3 py-2.5">
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-x-3 gap-y-2">
            <FinRow label="Satış" value={fmt(share.tutar)} />
            {hasFees ? (
              <>
                <FinRow label="Kesim" value={fmt(share.kesimUcreti ?? 0)} />
                <FinRow
                  label="Parçalama"
                  value={fmt(share.parcalamaUcreti ?? 0)}
                />
                <FinRow label="Taşıma" value={fmt(share.tasimaUcreti ?? 0)} />
                {(share.indirim ?? 0) > 0 && (
                  <FinRow
                    label="İndirim"
                    value={`-${fmt(share.indirim ?? 0)}`}
                    muted
                  />
                )}
                <FinRow label="Toplam Ücret" value={fmt(toplamUcret)} />
              </>
            ) : (
              <FinRow label="Toplam Ücret" value={fmt(share.tutar)} />
            )}
            <FinRow label="Ödenen" value={fmt(share.odenen)} />
            <FinRow
              label="Kalan / Bakiye"
              value={fmt(share.kalan)}
              accent={share.kalan !== 0}
              muted={share.kalan === 0}
            />
          </div>
        </div>

        {(share.satisFisNo || share.satisNotu) && (
          <div className="px-3 pb-2.5 pt-0 space-y-2">
            {share.satisFisNo && (
              <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-2">
                <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">Fiş No</p>
                <p className="font-body text-xs text-farm-brown">{share.satisFisNo}</p>
              </div>
            )}
            {share.satisNotu && (
              <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-2">
                <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">Satış Notu</p>
                <p className="font-body text-xs text-farm-brown whitespace-pre-wrap">{share.satisNotu}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 px-3 pb-2.5 pt-0 flex-wrap">
          <button
            type="button"
            onClick={() =>
              navigate("/panel/odeme-al", {
                state: {
                  customerId: matchedCustomer?.id,
                  selectedShareIds: [share.id],
                },
              })
            }
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-farm-green/10 border border-farm-green/25 text-farm-green font-body text-[10px] font-semibold hover:bg-farm-green hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-cash-line text-xs" />
            </div>
            Ödeme Al
          </button>

          <button
            type="button"
            onClick={() => setShowPaymentDrawer(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-farm-cream border border-farm-border text-farm-muted font-body text-[10px] font-semibold hover:border-farm-earth hover:text-farm-earth transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-file-list-3-line text-xs" />
            </div>
            Ödeme Geçmişi
          </button>

          <button
            type="button"
            onClick={() => setShowTransferModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-farm-earth/10 border border-farm-earth/20 text-farm-earth font-body text-[10px] font-semibold hover:bg-farm-earth hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-share-forward-line text-xs" />
            </div>
            Başka Hayvana Aktar
          </button>

          <button
            type="button"
            onClick={handleDeleteSale}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-600 font-body text-[10px] font-semibold hover:bg-red-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-close-circle-line text-xs" />
            </div>
            Satışı İptal Et
          </button>
        </div>
      </div>

      {showPaymentDrawer && (
        <SharePaymentDrawer
          share={share}
          animalId={animalId}
          onClose={() => setShowPaymentDrawer(false)}
        />
      )}

      {showTransferModal && (
        <ShareTransferModal
          share={share}
          sourceAnimalId={animalId}
          onClose={() => setShowTransferModal(false)}
        />
      )}
    </>
  );
}

function EmptySlotCard({
  slotNo,
  animalId,
  defaultKesimUcreti,
  defaultParcalamaUcreti,
  defaultTasimaUcreti,
}: {
  slotNo: number;
  animalId: number;
  defaultKesimUcreti?: number;
  defaultParcalamaUcreti?: number;
  defaultTasimaUcreti?: number;
}) {
  const navigate = useNavigate();

  return (
    <div className="bg-farm-cream border border-dashed border-farm-border rounded-farm overflow-hidden opacity-75">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-farm-border/50">
        <div className="flex items-center gap-2">
          <span className="font-body text-[10px] text-farm-muted font-semibold">
            Hisse {slotNo}
          </span>
          <span className="font-body text-xs text-farm-muted italic">
            — Satılmadı
          </span>
        </div>
        <span className="inline-block px-2 py-0.5 rounded-full bg-farm-border/40 text-farm-muted text-[10px] font-semibold">
          Boş
        </span>
      </div>

      {(defaultKesimUcreti ||
        defaultParcalamaUcreti ||
        defaultTasimaUcreti) && (
        <div className="px-3 py-2 flex items-center gap-3 flex-wrap">
          <span className="font-body text-[10px] text-farm-muted/70">
            Varsayılan ücretler:
          </span>
          {defaultKesimUcreti && (
            <span className="font-body text-[10px] text-farm-muted">
              Kesim {fmt(defaultKesimUcreti)}
            </span>
          )}
          {defaultParcalamaUcreti && (
            <span className="font-body text-[10px] text-farm-muted">
              Parçalama {fmt(defaultParcalamaUcreti)}
            </span>
          )}
          {defaultTasimaUcreti && (
            <span className="font-body text-[10px] text-farm-muted">
              Taşıma {fmt(defaultTasimaUcreti)}
            </span>
          )}
        </div>
      )}

      <div className="px-3 pb-2.5">
        <button
          type="button"
          onClick={() =>
            navigate("/panel/musteriler", {
              state: {
                fromAnimalSale: true,
                animalId,
                slotNo,
                defaultKesimUcreti,
                defaultParcalamaUcreti,
                defaultTasimaUcreti,
              },
            })
          }
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-farm-earth/10 border border-farm-earth/20 text-farm-earth font-body text-[10px] font-semibold hover:bg-farm-earth hover:text-white transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-user-add-line text-xs" />
          </div>
          Hisse Sat
        </button>
      </div>
    </div>
  );
}

interface Props {
  shares: ShareItem[];
  toplamHisse: number;
  animalId: number;
  defaultKesimUcreti?: number;
  defaultParcalamaUcreti?: number;
  defaultTasimaUcreti?: number;
}

export default function ShareDetailSection({
  shares,
  toplamHisse,
  animalId,
  defaultKesimUcreti,
  defaultParcalamaUcreti,
  defaultTasimaUcreti,
}: Props) {
  const usedSlotNos = useMemo(
    () =>
      shares
        .flatMap((share) => getShareSlotNos(share))
        .filter((value) => value > 0),
    [shares]
  );

  const emptySlotNos = useMemo(() => {
    const used = new Set(usedSlotNos);
    const result: number[] = [];

    for (let i = 1; i <= toplamHisse; i++) {
      if (!used.has(i)) {
        result.push(i);
      }
    }

    return result;
  }, [toplamHisse, usedSlotNos]);

  return (
    <div className="flex flex-col gap-2.5">
      {shares.map((share) => (
        <SoldShareCard key={share.id} share={share} animalId={animalId} />
      ))}

      {emptySlotNos.map((slotNo) => (
        <EmptySlotCard
          key={`empty-${slotNo}`}
          slotNo={slotNo}
          animalId={animalId}
          defaultKesimUcreti={defaultKesimUcreti}
          defaultParcalamaUcreti={defaultParcalamaUcreti}
          defaultTasimaUcreti={defaultTasimaUcreti}
        />
      ))}
    </div>
  );
}